import type { TOC } from '@ember/component/template-only';
import {
  PrismButton,
  PrismComboSelectInput,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { pageTitle } from 'ember-page-title';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import ReviewCardLoading from 'lumen/components/hotel/reviews/review-card-loading';
import HotelReviewsReviewDetail from 'lumen/components/hotel/reviews/review-detail/review-detail';
import HotelReviewsSidebar from 'lumen/components/hotel/reviews/sidebar';
import type HotelReviewsController from 'lumen/controllers/hotel/reviews';
import contextualPageTitle from 'lumen/helpers/contextual-page-title';

interface HotelReviewsSignature {
  Args: {
    controller: HotelReviewsController;
    model: unknown;
  };
}

import styles from './reviews.module.css';

<template>
  {{pageTitle (contextualPageTitle (t "menu.reviews"))}}
  <GeneralPageHeader>
    <div class="content-wrapper">
      <div class="flex flex-row items-center justify-between">
        <span class="flex items-center gap-200">
          <PrismComboSelectInput
            @buildLabelWithCount={{@controller.reviewStatusBuildLabelWithCount}}
            @onChange={{@controller.onReviewStatusChange}}
            @options={{@controller.reviewStatusOptions}}
            @selectAllLabel={{t "review_manager.filter.all_reviews"}}
            @value={{@controller.selectedReviewStatusOption}}
          />

          <PrismComboSelectInput
            @buildLabelWithCount={{@controller.scoreRangeBuildLabelWithCount}}
            @onChange={{@controller.onScoreRangeChange}}
            @options={{@controller.scoreRangeOptions}}
            @selectAllLabel={{t "review_manager.filter.all_score_ranges"}}
            @value={{@controller.selectedScoreRangeOption}}
          />

          <PrismComboSelectInput
            @buildLabelWithCount={{@controller.channelBuildLabelWithCount}}
            @onChange={{@controller.onChannelChange}}
            @options={{@controller.channelFilterOptions}}
            @selectAllLabel={{t "review_manager.filter.all_channels"}}
            @value={{@controller.selectedChannelOptions}}
          />
        </span>
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageMain>
    <div class="content-wrapper main-content clearfix overflow-hidden p-0">
      <div class="flex {{styles.main-content-wrapper}} w-full">

        <div class={{styles.sidebar}}>
          <HotelReviewsSidebar
            @isLoading={{@controller.isRequestingPageData}}
            @isRefreshDisabled={{@controller.isRefreshDisabled}}
            @onRefreshClick={{@controller.onReviewsRefreshClicked}}
            @otasWithErrors={{@controller.otasWithFetchErrors}}
            @reviews={{@controller.reviewsDataToDisplay}}
            @selectReviewForDetail={{@controller.selectReviewForDetail}}
            @selectedReviewId={{@controller.effectiveSelectedReviewId}}
          />
        </div>

        <div
          class="flex w-full justify-center pt-1000 pb-800
            {{styles.detail-container}}"
        >
          <div class="flex h-fit min-h-full w-[85%] flex-col">
            {{#if @controller.hasNoConnectedOtas}}
              {{! Empty state when no OTA channels are connected }}
              <div class="flex flex-1 items-center justify-center">
                <PrismMessage
                  @description={{t
                    "review_manager.empty_state.no_connected_otas.description"
                  }}
                  @illustration="100x100_disconnected"
                  @title={{t
                    "review_manager.empty_state.no_connected_otas.label"
                  }}
                  data-test-id="no-connected-otas-empty-state"
                >
                  <:primaryAction>
                    <PrismButton
                      @buttonType="primary"
                      @label={{t "review_agent.no_channels.cta"}}
                      @onClick={{@controller.contactCustomerCare}}
                    />
                  </:primaryAction>
                </PrismMessage>
              </div>
            {{else if @controller.isRequestingPageData}}
              <ReviewCardLoading />
            {{else}}
              {{#if @controller.reviewsDataToDisplay.length}}
                {{#if @controller.selectedReview}}
                  <HotelReviewsReviewDetail
                    @aiUsageData={{@controller.aiUsageData}}
                    @onAiUsageChange={{@controller.refreshAiUsage}}
                    @review={{@controller.selectedReview}}
                  />
                {{/if}}
              {{else}}
                {{#if @controller.expediaThirdPartySharingError}}
                  {{! Empty state for Expedia third-party sharing disabled }}
                  <div class="flex flex-1 items-center justify-center">
                    <PrismMessage
                      @description={{t
                        "review_manager.empty_state.expedia_sharing_disabled.description"
                      }}
                      @illustration="100x100_disconnected"
                      @title={{t
                        "review_manager.empty_state.expedia_sharing_disabled.title"
                      }}
                    >
                      <:primaryAction>
                        {{#if
                          @controller.expediaThirdPartySharingError.extranet_url
                        }}
                          <PrismButton
                            @buttonType="primary"
                            @label={{t
                              "review_manager.empty_state.expedia_sharing_disabled.action_button"
                            }}
                            @onClick={{@controller.openExpediaExtranet}}
                          />
                        {{/if}}
                      </:primaryAction>
                    </PrismMessage>
                  </div>
                {{else if @controller.isToReplyStatusFilterSelected}}
                  {{! Empty state in case there are no reviews that need replying }}
                  <div class="flex flex-1 items-center justify-center">
                    <PrismMessage
                      @description={{t
                        "review_manager.empty_state.to_reply.description"
                      }}
                      @illustration="100x100_success"
                      @title={{t "review_manager.empty_state.to_reply.label"}}
                    />
                  </div>
                {{else}}
                  {{! Empty state in case no reviews are available, or match the selected filters }}
                  <div class="flex flex-1 items-center justify-center">
                    <PrismMessage
                      @illustration="100x100_comment"
                      @title={{t "review_manager.empty_state.label"}}
                    />
                  </div>
                {{/if}}
              {{/if}}
            {{/if}}
          </div>
        </div>
      </div>
    </div>
  </GeneralPageMain>
</template> satisfies TOC<HotelReviewsSignature>;
